// src/pages/shop/ShopReports.jsx
//
// Страница генерации PDF-отчёта. Выбор периода + чекбоксы блоков → PDF.

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { reports as reportsApi } from '../../api';
import { useMyShop } from '../../hooks/useMyShop';
import { isModuleOn } from '../../config/modules';
import Icon from '../../components/Icon';
import BottomNav from '../../components/BottomNav';
import { useToast } from '../../hooks/useToast';
import { useShopNav } from './ShopHome';
import { generateBusinessReportPDF } from '../../utils/generateBusinessReportPDF';
import { savePDF } from '../../utils/savePDF';
import { Capacitor } from '@capacitor/core';

// Все блоки (id, ключ перевода)
export const BLOCKS = [
  { id: 'summary',      iconKey: 'chart' },
  { id: 'cashflow',     iconKey: 'wallet' },
  { id: 'inventory',    iconKey: 'package' },
  { id: 'sales',        iconKey: 'list' },
  { id: 'discounts',    iconKey: 'percent' },
  { id: 'returns',      iconKey: 'refresh' },
  { id: 'purchases',    iconKey: 'truck' },
  { id: 'writeoffs',    iconKey: 'trash' },
  { id: 'loyalty',      iconKey: 'star' },
  { id: 'topProducts',  iconKey: 'package' },
  { id: 'categories',   iconKey: 'folder' },
  { id: 'services',     iconKey: 'sparkles' },
  { id: 'customers',    iconKey: 'users' },
  { id: 'employees',    iconKey: 'user' },
  { id: 'tax',          iconKey: 'fileText' },
  { id: 'wholesale',    iconKey: 'truck' },
  { id: 'cafe',         iconKey: 'cashier' },
];

// Блок доступен магазину, только если включён соответствующий модуль/настройка.
// Блоки без привязки (сводка, продажи, налоги…) доступны всегда.
const BLOCK_MODULE = {
  cafe: 'waiter', wholesale: 'wholesale', services: 'services',
  returns: 'returns', purchases: 'purchases', writeoffs: 'writeoffs',
};
export function isReportBlockAvailable(id, shop) {
  if (id === 'loyalty') return shop?.loyaltyEnabled !== false;
  const mod = BLOCK_MODULE[id];
  if (!mod) return true;
  return isModuleOn(shop, mod);
}

export const HIDDEN_KEY = 'alsatuu_report_hidden_blocks';

const PRESETS = [
  { id: 'today',     days: 1 },
  { id: 'week',      days: 7 },
  { id: 'month',     days: 30 },
  { id: 'quarter',   days: 90 },
];

function dateForPreset(days) {
  const to = new Date();
  const from = new Date();
  if (days === 1) {
    from.setHours(0, 0, 0, 0);
  } else {
    from.setDate(from.getDate() - days);
  }
  return { from, to };
}

function formatLocalDateInput(d) {
  // YYYY-MM-DD для input[type=date]
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function ShopReports() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navItems = useShopNav();
  const { show, ToastView } = useToast();
  const shop = useMyShop();

  // Скрытые блоки (настраивается через шестерёнку). По умолчанию ничего не скрыто.
  const [hiddenBlocks] = useState(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_KEY);
      if (raw) { const a = JSON.parse(raw); if (Array.isArray(a)) return new Set(a); }
    } catch (e) { /* ignore */ }
    return new Set();
  });
  const shownBlocks = BLOCKS.filter(b =>
    isReportBlockAvailable(b.id, shop) && !hiddenBlocks.has(b.id)
  );

  const [preset, setPreset] = useState('month');
  const init = dateForPreset(30);
  const [fromDate, setFromDate] = useState(formatLocalDateInput(init.from));
  const [toDate, setToDate]     = useState(formatLocalDateInput(init.to));

  // По умолчанию выбраны самые полезные блоки; выбор запоминается между заходами
  const [selected, setSelected] = useState(() => {
    try {
      const saved = localStorage.getItem('alsatuu_report_blocks');
      if (saved) {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr) && arr.length) return new Set(arr);
      }
    } catch (e) { /* ignore */ }
    return new Set(['summary', 'topProducts', 'categories', 'tax']);
  });

  useEffect(() => {
    try { localStorage.setItem('alsatuu_report_blocks', JSON.stringify([...selected])); } catch (e) { /* ignore */ }
  }, [selected]);

  const [generating, setGenerating] = useState(false);

  const onPreset = (p) => {
    setPreset(p.id);
    const d = dateForPreset(p.days);
    setFromDate(formatLocalDateInput(d.from));
    setToDate(formatLocalDateInput(d.to));
  };

  const onDateChange = (which, value) => {
    if (which === 'from') setFromDate(value);
    else setToDate(value);
    setPreset(null); // снимаем активный preset когда юзер ввёл свои даты
  };

  const toggleBlock = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(shownBlocks.map(b => b.id)));
  const clearAll  = () => setSelected(new Set());

  const generate = async () => {
    if (selected.size === 0) {
      show(t('reports.errorNoBlocks'), 'error');
      return;
    }

    setGenerating(true);
    try {
      const from = new Date(fromDate + 'T00:00:00');
      const to   = new Date(toDate + 'T23:59:59');
      const blocks = shownBlocks.filter(b => selected.has(b.id)).map(b => b.id);
      if (blocks.length === 0) { show(t('reports.errorNoBlocks'), 'error'); setGenerating(false); return; }

      const data = await reportsApi.full({ from, to, blocks });
      const doc = await generateBusinessReportPDF(data, blocks);

      const blob = doc.output('blob');
      const _n = new Date();
      const _p = (x) => String(x).padStart(2, '0');
      const stamp = `${_n.getFullYear()}${_p(_n.getMonth() + 1)}${_p(_n.getDate())}-${_p(_n.getHours())}${_p(_n.getMinutes())}${_p(_n.getSeconds())}`;
      const filename = `report_${fromDate}_${toDate}_${stamp}.pdf`;

      const result = await savePDF(blob, filename);

      if (result.native) {
        show(t('reports.savedAndroid'));
      } else {
        show(t('reports.savedWeb'));
      }
    } catch (e) {
      console.error('Report generation failed:', e);
      show(e.message || t('reports.errorGenerate'), 'error');
    }
    setGenerating(false);
  };

  return (
    <div className="page" style={{ paddingBottom: 80 }}>
      {ToastView}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{t('reports.subtitle')}</p>
          <h1 className="page-title">{t('reports.pdfReport')}</h1>
        </div>
        <button className="back-btn" onClick={() => navigate('/shop/reports/settings')} title={t('reports.settingsTitle')}>
          <Icon name="settings" size={18} />
        </button>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Период */}
        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.08em', marginBottom: 8 }}>
          {t('reports.period').toUpperCase()}
        </p>

        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {PRESETS.map(p => (
            <button
              key={p.id}
              onClick={() => onPreset(p)}
              style={{
                padding: '8px 14px',
                borderRadius: 'var(--r-sm)',
                border: '1px solid',
                borderColor: preset === p.id ? 'var(--brand-primary)' : 'var(--border)',
                background: preset === p.id ? 'var(--brand-primary-soft)' : 'var(--bg-elevated)',
                color: preset === p.id ? 'var(--brand-primary)' : 'var(--fg)',
                fontSize: 13,
                fontWeight: preset === p.id ? 700 : 500,
                cursor: 'pointer',
              }}
            >
              {t(`reports.preset_${p.id}`)}
            </button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 18 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{t('reports.from')}</label>
            <input
              type="date"
              className="input"
              value={fromDate}
              onChange={e => onDateChange('from', e.target.value)}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{t('reports.to')}</label>
            <input
              type="date"
              className="input"
              value={toDate}
              onChange={e => onDateChange('to', e.target.value)}
            />
          </div>
        </div>

        {/* Блоки */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.08em' }}>
            {t('reports.blocks').toUpperCase()}  ·  {[...selected].filter(id => shownBlocks.some(b => b.id === id)).length}/{shownBlocks.length}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={selectAll}
              style={{ background: 'none', border: 'none', color: 'var(--brand-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              {t('reports.selectAll')}
            </button>
            <button
              onClick={clearAll}
              style={{ background: 'none', border: 'none', color: 'var(--fg-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              {t('reports.clearAll')}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 18 }}>
          {shownBlocks.map(b => {
            const checked = selected.has(b.id);
            return (
              <label
                key={b.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px',
                  background: checked ? 'var(--brand-primary-soft)' : 'var(--bg-subtle)',
                  border: '1px solid',
                  borderColor: checked ? 'var(--brand-primary)' : 'transparent',
                  borderRadius: 'var(--r-sm)',
                  cursor: 'pointer',
                  transition: 'all 120ms',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleBlock(b.id)}
                  style={{ width: 18, height: 18, flexShrink: 0 }}
                />
                <Icon name={b.iconKey} size={16} color={checked ? 'var(--brand-primary)' : 'var(--fg-muted)'} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: checked ? 'var(--brand-primary)' : 'var(--fg)' }}>
                    {t(`reports.block_${b.id}`)}
                  </p>
                  <p style={{ fontSize: 11, color: 'var(--fg-faint)', lineHeight: 1.3 }}>
                    {t(`reports.block_${b.id}_desc`)}
                  </p>
                </div>
              </label>
            );
          })}
        </div>

        <button
          onClick={generate}
          disabled={generating || selected.size === 0}
          className="btn btn-primary"
          style={{
            width: '100%',
            marginBottom: 16,
            opacity: (generating || selected.size === 0) ? 0.55 : 1,
          }}
        >
          {generating
            ? <><div className="spinner" style={{ width: 14, height: 14, marginRight: 8 }} />{t('reports.generating')}</>
            : <><Icon name="download" size={16} /><span style={{ marginLeft: 8 }}>{t('reports.generate')}</span></>
          }
        </button>

        <p style={{ fontSize: 11, color: 'var(--fg-faint)', textAlign: 'center', lineHeight: 1.5 }}>
          {Capacitor.isNativePlatform()
            ? t('reports.hintAndroid')
            : t('reports.hintWeb')}
        </p>
      </div>

      <BottomNav items={navItems} />
    </div>
  );
}
